import React, { useCallback, useContext, useMemo, useState } from 'react';

import { useSidebar } from '../layout/Sidebar';

const DRAWER_ANIMATION_TIMEOUT = 201;
const BREAKPOINT_MD = 768; // bootstrap variable --breakpoint-md value

type LayoutContext = {
  nav: {
    isAnimating: boolean;
    isOpen: boolean;
    toggle: () => void;
  };
  sidebar: ReturnType<typeof useSidebar>;
};

const LayoutContext = React.createContext<LayoutContext | null>(null);

export const LayoutProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const sidebar = useSidebar();
  const [isDrawerOpen, toggleDrawerState] = useState(() => window.innerWidth > BREAKPOINT_MD);
  const [isDrawerAnimating, setDrawerAnimating] = useState(false);

  const toggleDrawer = useCallback(() => {
    setDrawerAnimating(true);
    toggleDrawerState((state) => !state);
  }, []);

  if (isDrawerAnimating) {
    setTimeout(() => setDrawerAnimating(false), DRAWER_ANIMATION_TIMEOUT);
  }

  const value = useMemo(
    () => ({
      nav: {
        isAnimating: isDrawerAnimating,
        isOpen: isDrawerOpen,
        toggle: toggleDrawer,
      },
      sidebar,
    }),
    [isDrawerAnimating, isDrawerOpen, toggleDrawer, sidebar]
  );

  return <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>;
};

export const useLayout = () => {
  const layoutContext = useContext(LayoutContext);

  if (!layoutContext) {
    throw new Error('useLayout must be used within LayoutProvider.');
  }

  return layoutContext;
};
